// src/utils/problemesEquipement.ts — SCE Issues
// La liste des problèmes de la fiche équipement (10/10) : filtres par statut,
// métier (« mon métier » compris) et badge qualité, et ordre de lecture —
// ce qui reste à traiter d'abord, le plus récent en tête ; puis ce qui est
// terminé, la dernière clôture en tête.

import type { BadgeCloture, Issue, Metier, Statut } from '@/types/issue.types'
import { BADGES_CLOTURE } from '@/types/issue.types'

export interface FiltresProblemes {
  statuts: Statut[]
  metiers: Metier[]
  badges: BadgeCloture[]
}

export const FILTRES_VIDES: FiltresProblemes = { statuts: [], metiers: [], badges: [] }

/** Un groupe vide ne filtre pas ; deux groupes se cumulent (ET), les valeurs d'un groupe s'additionnent (OU). */
export function filtrerProblemes(issues: readonly Issue[], f: FiltresProblemes): Issue[] {
  return issues.filter(
    (i) =>
      (f.statuts.length === 0 || f.statuts.includes(i.statut)) &&
      (f.metiers.length === 0 || f.metiers.includes(i.metier)) &&
      (f.badges.length === 0 || (i.badgesCloture ?? []).some((b) => f.badges.includes(b))),
  )
}

export function nbFiltres(f: FiltresProblemes): number {
  return f.statuts.length + f.metiers.length + f.badges.length
}

export function estTermine(i: Issue): boolean {
  return i.statut === 'Clos' || i.statut === 'Annulé'
}

/** À traiter d'abord (plus récent en tête), puis terminés (dernière clôture en tête). */
export function trierProblemes(issues: readonly Issue[]): { aTraiter: Issue[]; termines: Issue[] } {
  const date = (i: Issue): string => (estTermine(i) ? i.dateCloture || i.dateDeclaration : i.dateDeclaration) || ''
  const recent = (a: Issue, b: Issue): number => date(b).localeCompare(date(a))
  return {
    aTraiter: issues.filter((i) => !estTermine(i)).sort(recent),
    termines: issues.filter(estTermine).sort(recent),
  }
}

/** Les choix de filtre utiles : seulement les valeurs présentes sur la machine, avec leur nombre. */
export function optionsFiltres(issues: readonly Issue[]): {
  statuts: { valeur: Statut; nb: number }[]
  metiers: { valeur: Metier; nb: number }[]
  badges: { valeur: BadgeCloture; nb: number }[]
} {
  const compter = <T,>(valeurs: T[]): Map<T, number> => {
    const m = new Map<T, number>()
    for (const v of valeurs) m.set(v, (m.get(v) ?? 0) + 1)
    return m
  }
  const statuts = compter(issues.map((i) => i.statut))
  const metiers = compter(issues.map((i) => i.metier))
  const badges = compter(issues.flatMap((i) => i.badgesCloture ?? []))
  const ORDRE_STATUTS: Statut[] = ['Ouvert', 'En cours', 'Clos', 'Annulé']
  return {
    statuts: ORDRE_STATUTS.filter((s) => statuts.has(s)).map((s) => ({ valeur: s, nb: statuts.get(s)! })),
    metiers: [...metiers].map(([valeur, nb]) => ({ valeur, nb })).sort((a, b) => a.valeur.localeCompare(b.valeur)),
    badges: BADGES_CLOTURE.filter((b) => badges.has(b)).map((b) => ({ valeur: b, nb: badges.get(b)! })),
  }
}

/** « il y a 3 j », « aujourd'hui » ; pour un problème terminé : « clos le 2 oct. ». */
export function quandProbleme(i: Issue, maintenant: Date = new Date()): string {
  const termine = estTermine(i)
  const iso = termine ? i.dateCloture || i.dateDeclaration : i.dateDeclaration
  const d = new Date(iso ?? '')
  if (Number.isNaN(d.getTime())) return ''
  if (termine) {
    const jour = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', ...(d.getFullYear() !== maintenant.getFullYear() ? { year: 'numeric' } : {}) })
    return `${i.statut === 'Annulé' ? 'annulé' : 'clos'} le ${jour}`
  }
  const jours = Math.floor((maintenant.getTime() - d.getTime()) / 86_400_000)
  if (jours <= 0) return 'aujourd’hui'
  return `il y a ${jours} j`
}
